import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, LifeBuoy, Plus, Search } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import APITable from "../../components/Tables/APITable";
import Pagination from "../../components/Pagination/Pagination";
import Input from "../../components/Input/Input";
import Button from "../../components/Button/Button";
import Notification from "../../components/Notification/Notification";
import DateRangePicker from "../../components/Calender/DateRangePicker";
import { TableSkeleton } from "../../components/Skeleton/StatsSkeleton";
import ViewTicketModal from "../../components/HelpDesk/ViewTicketModal";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./HelpDesk.module.scss";

const STATUS_OPTIONS = [
  { label: "All Statuses", value: "" },
  { label: "Open", value: "Open" },
  { label: "In Progress", value: "InProgress" },
  { label: "Resolved", value: "Resolved" },
  { label: "Closed", value: "Closed" },
];

const formatDateTime = (iso) => {
  if (!iso) return "--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const HelpDesk = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [datePickerKey, setDatePickerKey] = useState(0);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  const [viewModal, setViewModal] = useState({ open: false, id: null });

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (status) params.set("status", status);
      if (search) params.set("search", search);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);

      const res = await apiGet(
        "catalog",
        `/help-desk/tickets/mine?${params.toString()}`,
      );

      if (res?.status === "success") {
        const payload = res.data || {};
        const list = Array.isArray(payload.data) ? payload.data : [];
        setRows(list);
        setPagination(
          payload.pagination || {
            page,
            limit,
            total: list.length,
            totalPages: 1,
          },
        );
      } else {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to load tickets",
          }),
        );
      }
    } catch (err) {
      showNotification(getErrorMessage(err, "Failed to load tickets"), "error");
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, search, fromDate, toDate]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleResetFilters = () => {
    setStatus("");
    setSearchInput("");
    setSearch("");
    setFromDate("");
    setToDate("");
    setPage(1);
    setDatePickerKey((k) => k + 1);
  };

  const tableData = rows.map((row) => ({
    ...row,
    onView: () => setViewModal({ open: true, id: row.id }),
  }));

  const columns = [
    { label: "Ticket No.", key: "ticketNo" },
    { label: "Subject", key: "subject" },
    { label: "Product", key: "productName" },
    { label: "Status", key: "status" },
    { label: "Due At", key: "dueAt" },
    { label: "Created", key: "createdAt" },
    { label: "Actions", key: "action" },
  ];

  return (
    <div className={styles["helpdesk__page"]}>
      {notification && (
        <div className={styles["helpdesk__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["helpdesk__header"]}>
        <div className={styles["helpdesk__icon-wrapper"]}>
          <LifeBuoy size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["helpdesk__title-group"]}>
          <h2 className={styles["helpdesk__title"]}>Help Desk</h2>
          <p className={styles["helpdesk__subtitle"]}>
            Track all support tickets raised by your organization.
          </p>
        </div>
        <div className={styles["helpdesk__header-actions"]}>
          <Button
            text="Raise Ticket"
            icon={Plus}
            onClick={() => navigate("/help-desk/raise")}
          />
        </div>
      </div>

      {/* CARD */}
      <div className={styles["helpdesk__card"]}>
        {/* FILTERS */}
        <div className={styles["helpdesk__filter-grid"]}>
          <Input
            type="select"
            placeholder="All Statuses"
            value={status}
            options={STATUS_OPTIONS}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          />
          <div className={styles["helpdesk__search-group"]}>
            <Input
              type="search"
              placeholder="Search tickets…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
            <Button text="Search" icon={Search} onClick={handleSearch} />
          </div>
        </div>

        <div className={styles["helpdesk__action-row"]}>
          <div className={styles["helpdesk__showing-text"]}>
            Showing {rows.length} of {pagination.total}
          </div>
          <div className={styles["helpdesk__filters"]}>
            <DateRangePicker
              key={datePickerKey}
              maxDate={new Date()}
              onChange={(type, value) => {
                if (type === "from") {
                  setFromDate(value);
                  setPage(1);
                }
                if (type === "to") {
                  setToDate(value);
                  setPage(1);
                }
              }}
            />
            {(status || search || fromDate || toDate) && (
              <button
                type="button"
                className={styles["helpdesk__reset-btn"]}
                onClick={handleResetFilters}
              >
                Reset filters
              </button>
            )}
          </div>
        </div>

        {/* TABLE */}
        <div className={styles["helpdesk__table-wrapper"]}>
          {loading ? (
            <TableSkeleton columns={7} rows={5} />
          ) : (
            <APITable
              columns={columns}
              data={tableData}
              renderCell={(row, key) => {
                if (key === "status") {
                  const tone =
                    row.status === "Open"
                      ? "open"
                      : row.status === "InProgress"
                        ? "progress"
                        : row.status === "Resolved"
                          ? "resolved"
                          : "closed";
                  return (
                    <span
                      className={`${styles["helpdesk__status-badge"]} ${
                        styles[`helpdesk__status-badge--${tone}`]
                      }`}
                    >
                      {row.status || "--"}
                    </span>
                  );
                }
                if (key === "dueAt") return formatDateTime(row.dueAt);
                if (key === "createdAt") return formatDateTime(row.createdAt);
                if (key === "action") {
                  return (
                    <div className={styles["helpdesk__action-cell"]}>
                      <Eye
                        size={20}
                        className={styles["helpdesk__action-icon"]}
                        onClick={row.onView}
                        aria-label="View Ticket"
                        role="button"
                        tabIndex={0}
                      />
                    </div>
                  );
                }
                return row[key] || "--";
              }}
            />
          )}
        </div>

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          rowsPerPage={limit}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setLimit(value);
            setPage(1);
          }}
        />
      </div>

      <ViewTicketModal
        open={viewModal.open}
        ticketId={viewModal.id}
        onClose={() => setViewModal({ open: false, id: null })}
        onError={(msg) => showNotification(msg, "error")}
      />
    </div>
  );
};

export default HelpDesk;
